/**
 * Task-Invite Token Service
 *
 * Stores the external reviewer's task-invite JWT in localStorage. Used by:
 *   - the /review/:orgKey landing page (storing tokens on OTP verify)
 *   - the SPA's axios interceptor (attaching the token to /v2/tasks/* and
 *     /v2/reports/* requests when the SPA detects a reviewer session)
 */

const ACCESS_TOKEN_KEY = 'task_invite_access_token';
const REFRESH_TOKEN_KEY = 'task_invite_refresh_token';
const ORG_ID_KEY = 'task_invite_org_id';
const EMAIL_KEY = 'task_invite_email';

export interface TaskInviteTokens {
    accessToken: string;
    refreshToken: string;
    expiresAt: string;
    organizationId: string;
    email: string;
}

class TaskInviteTokenService {
    static store(t: TaskInviteTokens): void {
        try {
            localStorage.setItem(ACCESS_TOKEN_KEY, t.accessToken);
            localStorage.setItem(REFRESH_TOKEN_KEY, t.refreshToken);
            localStorage.setItem(ORG_ID_KEY, t.organizationId);
            localStorage.setItem(EMAIL_KEY, t.email);
        } catch (e) {
            console.error('Error storing task-invite tokens:', e);
        }
    }

    static getAccessToken(): string | null {
        try {
            return localStorage.getItem(ACCESS_TOKEN_KEY);
        } catch {
            return null;
        }
    }

    static getRefreshToken(): string | null {
        try {
            return localStorage.getItem(REFRESH_TOKEN_KEY);
        } catch {
            return null;
        }
    }

    static getOrgId(): string | null {
        try {
            return localStorage.getItem(ORG_ID_KEY);
        } catch {
            return null;
        }
    }

    static getEmail(): string | null {
        try {
            return localStorage.getItem(EMAIL_KEY);
        } catch {
            return null;
        }
    }

    static getAuthHeader(): string | null {
        const token = this.getAccessToken();
        return token ? `Bearer ${token}` : null;
    }

    static isActive(): boolean {
        return Boolean(this.getAccessToken() && this.getOrgId() && this.getEmail());
    }

    /** Returns true if we successfully refreshed; caller can retry the original request. */
    static async refreshAccessToken(): Promise<boolean> {
        const refreshToken = this.getRefreshToken();
        if (!refreshToken) return false;
        try {
            const { api } = await import('@/lib/http');
            const { data } = await api.post<TaskInviteTokens>('/v2/tasks/auth/refresh', {
                refreshToken,
            });
            this.store({
                accessToken: data.accessToken,
                refreshToken: data.refreshToken,
                expiresAt: data.expiresAt,
                organizationId: this.getOrgId() || '',
                email: this.getEmail() || '',
            });
            return true;
        } catch {
            return false;
        }
    }

    static clear(): void {
        try {
            localStorage.removeItem(ACCESS_TOKEN_KEY);
            localStorage.removeItem(REFRESH_TOKEN_KEY);
            localStorage.removeItem(ORG_ID_KEY);
            localStorage.removeItem(EMAIL_KEY);
        } catch {
            /* ignore */
        }
    }
}

export default TaskInviteTokenService;
